import * as React from 'react';

import Box from '@mui/material/Box';

import { ViewBs4EventPeriod } from './Bs4ReportCalendarViewBs4Event';
import { toDate, toDateTime } from '../../Bs4Components/Bs4Utils'
import { useDroppable } from '@dnd-kit/core';

import {    
  addDays, addMinutes, 
  startOfDay, endOfDay,  
  compareAsc,
  eachDayOfInterval,
  format,
  differenceInMilliseconds,
  addMilliseconds,
  max, min,
  differenceInCalendarDays,
  setHours, getHours,
  setMinutes, getMinutes,
} from "date-fns";
import { EnergySavingsLeafTwoTone } from '@mui/icons-material';

const DROPPABLE = 'droppable';

const getEl = (startPeriod, endPeriod, bs4Event, background) => {
  const bs4EventStart = max([startPeriod, startOfDay(bs4Event.start)]);
  const bs4EventEnd = min([endPeriod, endOfDay(bs4Event.end)]);
  
  return {
    background: background,
    bs4Event: bs4Event,
    start: bs4EventStart,
    end: bs4EventEnd,
  };
}

const getBackgroundBs4Events = (startPeriod, endPeriod, bs4EventArray) => {
  const elBackgroundArray = bs4EventArray.reduce((viewBackgroundArray, bs4Event) => {
    if (!bs4Event.layer) return viewBackgroundArray;    
    const el = getEl(startPeriod, endPeriod, bs4Event, true);    
    viewBackgroundArray.push(el); 
    return viewBackgroundArray;
  }, []);
  return elBackgroundArray;
}

const getDateKey_Y_D = (date) => format(date, 'Y-D', {useAdditionalDayOfYearTokens: true});

const setOtherBs4Events = (otherDays, el) => {
  eachDayOfInterval({start: el.start, end: el.end}).forEach(date => {
    const key = getDateKey_Y_D(date);     
    const start = startOfDay(date);
    const end = endOfDay(date);
    const nel = {...el};
    nel.start = start;
    nel.end = end;
    let els = otherDays[key];
    if (els) {
      els.elArray.push(nel);
    } else {      
      otherDays[key] = {
        elArray: [nel],
        start: start,
        end: end,
      }
    }      
  });
}

const getSortedBs4Events = (startPeriod, endPeriod, bs4EventArray, maxLine) => {
  const elArray = bs4EventArray.reduce((elArray, bs4Event) => {
    if (bs4Event.layer) return elArray;    
    const el = getEl(startPeriod, endPeriod, bs4Event, false);
    elArray.push(el);
    return elArray;
  }, []);
      
  const elSorted = elArray.sort((a, b) => {
    let comparison = (b.end.getTime() - b.start.getTime()) - (a.end.getTime() - a.start.getTime());
    if (comparison === 0) comparison = a.start.getTime() - b.start.getTime();
    if (comparison === 0) comparison = b.end.getTime() - a.end.getTime();
    return comparison;
  });    

  const lineArray = [];
  const otherLineArray = [];
  for (const el of elSorted) {
    let pushed = false;
    for (let i = 0; i < lineArray.length; i++) {
      const line = lineArray[i];
      let foul = false;
      for (const elp of line) {
        if (!(el.end.getTime() < elp.start.getTime() || el.start.getTime() > elp.end.getTime())) {
          foul = true;
          break;
        };
      }

      if (!foul) {
        line.push(el);
        pushed = true;
        break;
      }
    }
    
    if (!pushed) {
      if (lineArray.length > maxLine - 1) {
        otherLineArray.push(el);
      } else {
        lineArray.push([el]);
      }
    }
  }

  if (otherLineArray.length === 0) {    
    return lineArray;
  }

  const otherDays = {};
  for (const el of otherLineArray) {
    setOtherBs4Events(otherDays, el);
  }

  const lastLineArray = lineArray.pop() || [];
  let lastLine = [];
  for (const el of lastLineArray) {
    let ok = true;
    const eachDay = eachDayOfInterval({start: el.bs4Event.start, end: el.bs4Event.end});
    for (const date of eachDay) {
      if (otherDays[getDateKey_Y_D(date)]) {
        ok = false;
        break;
      }
    }
    if (!ok) {
      setOtherBs4Events(otherDays, el);
    } else {
      lastLine.push(el);
    }
  }
  
  for (const [key, els] of Object.entries(otherDays)) lastLine.push(els);    

  if (lastLine.length > 0) lineArray.push(lastLine);
  return lineArray;
}

export const ViewPeriodBackground = (props) => {  
  const viewArray = React.useMemo(() => {
    return getBackgroundBs4Events(props.startPeriod, props.endPeriod, props.bs4EventArray);
  }, [props.startPeriod, props.endPeriod, props.bs4EventArray])  

  // console.log('...Bs4ReportCalendarViewPeriod...Background...', props, viewBackgroundArray);

  return (
    <Box
      id='Box_Bs4ReportCalendarViewPeriod_Background'
      position='relative'
      sx={{...props.sx, pointerEvents: 'none'}}
      overflow='visible'
    >        
      {viewArray.map((el, index) => {
        return (                    
          <ViewBs4EventPeriod               
            key={String(index)} 
            startPeriod={props.startPeriod}
            endPeriod={props.endPeriod}
            el={el}
            line={0}
            bs4kartotekaid={props.bs4KartotekaId} 
            onRowClick={props.onRowClick}
            onChangeView={props.onChangeView}
            bs4Calendar={props.bs4Calendar}
          />
        )
      })}      
    </Box>
  )
}

export const ViewPeriodForeground = (props) => {
  const [periodHeight, setPeriodHeight] = React.useState(0);
  const lineHeight = 24;
  const startLine = props.startLine || 0;
  const maxLine = React.useMemo(() => {
    const maxLine = props.maxLine || 0;    
    if (startLine >= maxLine) {
      return parseInt(periodHeight / lineHeight) - startLine;
    }
    return maxLine;
  }, [periodHeight, startLine, props.startLine, props.maxLine]) 
  const lineArray = React.useMemo(() => {
    return getSortedBs4Events(props.startPeriod, props.endPeriod, props.bs4EventArray, maxLine);
  }, [props.startPeriod, props.endPeriod, props.bs4EventArray, maxLine])

  const activeLineArray = React.useMemo(() => {
    const bs4Event = props.active?.props?.el?.bs4Event;    
    if (!bs4Event || compareAsc(bs4Event.end, props.startPeriod) < 0 || compareAsc(bs4Event.start, props.endPeriod) > 0) return null;
    return [[getEl(props.startPeriod, props.endPeriod, bs4Event, false)]];
  }, [props.startPeriod, props.endPeriod, props.active, props.activeChangeTime])  

  const refPeriod = React.useRef(null);    
  const minHeight = lineHeight * Math.max(1, lineArray.length);    

  const setBs4ActiveMove = React.useCallback((active, over, propsEnd) => {    
    if (! active?.props?.el) return;
    const el = active.props.el;

    if (!el.bs4Event.chtime) return;
    
    if (!over) {      
      if (propsEnd) {
        if (el.bs4Event.move.cursor) {
          propsEnd.bs4EventResize(el.bs4Event);
        } else {
          propsEnd.bs4EventDrop(el.bs4Event);
        }
      }
      return;
    }
    
    const rect = over.rect;
    const pointerCoordinates = over.data.current.droppableRef.pointerCoordinates;
    let moveDate = addMilliseconds(props.startPeriod, differenceInMilliseconds(props.endPeriod, props.startPeriod) / rect.width * (pointerCoordinates.x - rect.left));
    const differenceInCalendarDaysMove = differenceInCalendarDays(moveDate, el.bs4Event.move.moveDate);
    moveDate = addDays(el.bs4Event.move.moveDate, differenceInCalendarDaysMove);

    if (el.bs4Event.move.cursor) {
      if (el.bs4Event.move.cursor === 'n' || el.bs4Event.move.cursor === 'w') {        
        const start = addDays(el.bs4Event.move.start, differenceInCalendarDaysMove);
        if (compareAsc(start, el.bs4Event.end) > 0) return;
        if (compareAsc(start, el.bs4Event.start) === 0) return;
        el.bs4Event.start = start;
        el.bs4Event.duration = differenceInMilliseconds(el.bs4Event.end, el.bs4Event.start);
      } else if (el.bs4Event.move.cursor === 's' || el.bs4Event.move.cursor === 'e') {
        const end = addDays(el.bs4Event.move.end, differenceInCalendarDaysMove);
        if (compareAsc(el.bs4Event.start, end) > 0) return;
        if (compareAsc(end, el.bs4Event.end) === 0) return;
        el.bs4Event.end = end;
        el.bs4Event.duration = differenceInMilliseconds(el.bs4Event.end, el.bs4Event.start);
      }
    } else {             
      const start = addDays(el.bs4Event.move.start, differenceInCalendarDaysMove);
      if (compareAsc(start, el.bs4Event.start) === 0) return;
      el.bs4Event.start = start;
      el.bs4Event.end = addDays(el.bs4Event.move.end, differenceInCalendarDaysMove);
    }
    if (navigator.vibrate) navigator.vibrate(1);
    
  }, [props.startPeriod, props.endPeriod]);

  const [droppableRef, setDroppableRef] = React.useState({})

  const {isOver, active, setNodeRef, node} = useDroppable({
    id: DROPPABLE+'_PERIOD_'+toDate(props.startPeriod),
    data: {
      setBs4ActiveMove: setBs4ActiveMove,
      droppableRef: droppableRef,
    }
  });    

  const percentNow = React.useMemo(() => {
    const now = new Date();
    const startPeriodTime = props.startPeriod.getTime() || 1;
    const endPeriodTime = props.endPeriod.getTime() || 1;
    
    const nowTime = now.getTime();
  
    const percentNow = ((1.0 * (nowTime - startPeriodTime)) / (endPeriodTime - startPeriodTime)) * 100.0;
    if (percentNow < 1 || percentNow > 100) return null;
    return percentNow;
  }, [props.startPeriod, props.endPeriod])

  React.useEffect(() => {
    const resizeObserver = new ResizeObserver((e) => {
      const rect = refPeriod.current.getBoundingClientRect();
      setPeriodHeight(rect.height);
    });
    if (refPeriod.current) resizeObserver.observe(refPeriod.current);
    
    return () => {
      resizeObserver.disconnect();
    }
  }, [refPeriod.current, setPeriodHeight]);

  // console.log('...???...Bs4ReportCalendarViewPeriod...', percentNow, props);

  return (
    <Box
      id='Box_Bs4ReportCalendarViewPeriod'
      ref={node => {
        refPeriod.current = node;
        setNodeRef(node);
      }}
      minHeight={minHeight}
      position='relative'
      sx={{
        ...props.sx,
      }}
      overflow='visible'
    > 
      {percentNow 
        ? 
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: percentNow+'%',
            width: '0px',
            userSelect: 'none',        
            pointerEvents: 'none',
            borderLeft: '3px dashed #ff000022',
            zIndex: 200,
          }}          
        >
        </Box> 
        : null
      }
      {lineArray.map((line, index) => {
        return line.map((el, indexEl) => {
          if (el.bs4Event?.move?.active) return null;
          return (                    
            <ViewBs4EventPeriod 
              key={String(index)+'_'+String(indexEl)+el.start+el.end}
              startPeriod={props.startPeriod}
              endPeriod={props.endPeriod}
              el={el}
              line={startLine + index}
              bs4kartotekaid={props.bs4KartotekaId} 
              onRowClick={props.onRowClick}
              onChangeView={props.onChangeView}
              bs4Calendar={props.bs4Calendar}
            />
          )
        })}
      )}
      {activeLineArray ? activeLineArray.map((line, index) => {        
        return line.map((el, indexEl) => {
          return (                    
            <ViewBs4EventPeriod
              key={'active_'+String(index)+'_'+String(indexEl)+el.start+el.end} 
              startPeriod={props.startPeriod}
              endPeriod={props.endPeriod}
              el={el}
              line={startLine + index}
              bs4kartotekaid={props.bs4KartotekaId} 
              onRowClick={props.onRowClick}
              onChangeView={props.onChangeView}
              active={props.active}
              bs4Calendar={props.bs4Calendar}
            />
          )
        })}
      ) : null }
    </Box>
  )
}